import { showErrorNotification } from '@vrplatform/theme';
import { useAccountingConnectionQuery } from '@vrplatform/data';
import { parseAsString, useQueryState } from '@vrplatform/query-params';
import { type Maybe, day } from '@vrplatform/utils';
import { useMemo } from 'react';
import { useAutomationHistoryDrawer } from '../../../../hooks';
import { useAutomationsQuery } from '@vrplatform/data';

export function useStatementRunDrawer() {
  const [queryDate, setQueryDate] = useQueryState('date', parseAsString);

  const { setAutomationId } = useAutomationHistoryDrawer();
  const { data: automations = [], isLoading: loadingAutomations } =
    useAutomationsQuery();
  const { accounting } = useAccountingConnectionQuery();
  const accountingConnectionId = accounting?.id;

  const { statementAutomations, statementBillAutomations } = useMemo(() => {
    const statementAutomations = (automations || [])
      ?.filter(
        (x) =>
          x.template.uniqueRef?.toLowerCase().endsWith('ownerstatements') &&
          x.status === 'active' &&
          Object.values(x.connections).includes(accountingConnectionId || '')
      )
      .map((a) => ({ automationId: a.automationId, status: a.status }));

    const statementBillAutomations = (automations || [])
      ?.filter(
        (x) =>
          x.template.uniqueRef?.toLowerCase().endsWith('ownerstatementbills') &&
          x.status === 'active' &&
          Object.values(x.connections).includes(accountingConnectionId || '')
      )
      .map((a) => ({ automationId: a.automationId, status: a.status }));

    return {
      statementAutomations,
      statementBillAutomations,
    };
  }, [automations, accountingConnectionId]);

  return {
    statementAutomations,
    statementBillAutomations,
    loadingAutomations,
    open: (date?: string) => {
      if (!queryDate) {
        setQueryDate(date || day().format('YYYY-MM-01'));
      }

      const automationId = getStatementAutomationId(statementAutomations);

      // Error message is thrown in helper function
      if (!automationId) return;

      setAutomationId(automationId);
    },
  };
}

function getStatementAutomationId(
  statementAutomations: {
    automationId: string;
    status: Maybe<string>;
  }[]
) {
  if (!statementAutomations?.length)
    return showErrorNotification({
      title: 'Missing owner statement automation',
      message:
        'Please create an owner statement automation to generate statements.',
    });

  const activeStatementAutomations = statementAutomations.filter(
    ({ status }) => status === 'active'
  );

  if (activeStatementAutomations.length > 1) {
    return showErrorNotification({
      title: 'Multiple active owner statement automations',
      message:
        'Please deactivate all but one owner statement automation to generate statements.',
    });
  }

  return (
    activeStatementAutomations[0]?.automationId ||
    statementAutomations[0]?.automationId
  );
}
